import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import HomePage from './pages/public/HomePage';
import RosterProfile from './pages/public/RosterProfile';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ActivateAccount from './pages/auth/ActivateAccount';
import CompleteRegistration from './pages/auth/CompleteRegistration';
import PublishingAccessRequired from './pages/auth/PublishingAccessRequired';
import PortalLayout from './layouts/PortalLayout';
import PortalHome from './pages/portal/PortalHome';
import Analytics from './pages/portal/Analytics';
import Songs from './pages/portal/Songs';
import Statements from './pages/portal/Statements';
import Documents from './pages/portal/Documents';
import Help from './pages/portal/Help';
import Account from './pages/portal/Account';
import AdminGuard from './guards/AdminGuard';
import AdminRoster from './pages/portal/admin/AdminRoster';
import AdminRosterEdit from './pages/portal/admin/AdminRosterEdit';
import AdminInvitations from './pages/portal/admin/AdminInvitations';
import AdminUsers from './pages/portal/admin/AdminUsers';
import TermsOfUse from './pages/public/TermsOfUse';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import NotFound from './pages/NotFound';
import AuthGuard from './guards/AuthGuard';

export default function App() {
  useEffect(() => {
    const load = (src, integrity, onload) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        if (onload) onload();
        return;
      }
      const s = document.createElement('script');
      s.src = src;
      s.type = 'text/javascript';
      if (integrity) {
        s.integrity = integrity;
        s.crossOrigin = 'anonymous';
      }
      s.onload = onload;
      document.body.appendChild(s);
    };
    load(
      'https://d3e54v103j8qbb.cloudfront.net/js/jquery-3.5.1.min.dc5e7f18c8.js',
      'sha256-9/aliU8dGd2tb6OSsuzixeV4y/faTqgFtohetphbbj0=',
      () => load('/woss-publishing-template/js/webflow.js', null, () => {
        load('https://cdn.prod.website-files.com/gsap/3.15.0/gsap.min.js', null, () => {
          load('https://cdn.prod.website-files.com/gsap/3.15.0/SplitText.min.js', null, null);
          load('https://cdn.prod.website-files.com/gsap/3.15.0/ScrollTrigger.min.js', null, null);
        });
      })
    );
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/" element={<HomePage />} />
        <Route path="/roster/:slug" element={<RosterProfile />} />

        {/* Legal */}
        <Route path="/legal/terms" element={<TermsOfUse />} />
        <Route path="/legal/privacy" element={<PrivacyPolicy />} />

        {/* Auth */}
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/activate" element={<ActivateAccount />} />
        <Route path="/auth/complete-registration" element={<CompleteRegistration />} />
        <Route path="/auth/access-required" element={<PublishingAccessRequired />} />

        {/* Legacy auth redirects */}
        <Route path="/login" element={<Navigate to="/auth/login" replace />} />
        <Route path="/forgot-password" element={<Navigate to="/auth/forgot-password" replace />} />

        {/* Protected portal */}
        <Route
          path="/app/portal"
          element={
            <AuthGuard>
              <PortalLayout />
            </AuthGuard>
          }
        >
          <Route index element={<Navigate to="/app/portal/home" replace />} />
          <Route path="home" element={<PortalHome />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="songs" element={<Songs />} />
          <Route path="statements" element={<Statements />} />
          <Route path="documents" element={<Documents />} />
          <Route path="help" element={<Help />} />
          <Route path="account" element={<Account />} />

          {/* Admin — wrapped individually so PortalLayout chrome still renders */}
          <Route path="admin" element={<Navigate to="/app/portal/admin/roster" replace />} />
          <Route path="admin/roster" element={<AdminGuard><AdminRoster /></AdminGuard>} />
          <Route path="admin/roster/new" element={<AdminGuard><AdminRosterEdit /></AdminGuard>} />
          <Route path="admin/roster/:id/edit" element={<AdminGuard><AdminRosterEdit /></AdminGuard>} />
          <Route path="admin/invitations" element={<AdminGuard><AdminInvitations /></AdminGuard>} />
          <Route path="admin/users" element={<AdminGuard><AdminUsers /></AdminGuard>} />
        </Route>

        {/* Legacy portal redirects */}
        <Route path="/portal" element={<Navigate to="/app/portal/home" replace />} />
        <Route path="/portal/*" element={<Navigate to="/app/portal/home" replace />} />

        {/* Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
